import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  type SupportedLocale,
} from "./locale";

export const BLOG_SITE_URL = "https://alchema.eu";

export type BlogPageType =
  | "index"
  | "editorial"
  | "tag"
  | "pillar"
  | "country"
  | "city"
  | "role"
  | "visa"
  | "university";

export interface BlogUrlInput {
  locale: string;
  pageType: BlogPageType | string;
  slug?: string;
  countryCode?: string | null;
}

export interface BlogUrl {
  locale: SupportedLocale;
  path: string;
  absolute: string;
}

export interface BlogAlternateInput extends BlogUrlInput {
  locale: string;
}

export interface BlogLanguageLink {
  hrefLang: SupportedLocale | "x-default";
  href: string;
}

export function isSupportedBlogLocale(locale: string): locale is SupportedLocale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(locale);
}

export function assertSupportedBlogLocale(locale: string): SupportedLocale {
  if (!isSupportedBlogLocale(locale)) {
    throw new RangeError(`Unsupported blog locale: ${locale}`);
  }
  return locale;
}

function requireSegment(value: string | null | undefined, label: string): string {
  const normalized = value?.replace(/^\/+|\/+$/g, "");
  if (!normalized) throw new TypeError(`Missing blog ${label}`);
  return normalized;
}

/** Existing de-localized route-tree path for a blog page. */
export function getUnlocalizedBlogPath(
  input: Omit<BlogUrlInput, "locale"> | BlogUrlInput,
): string {
  const pageType = input.pageType;

  if (pageType === "index") return "/blog";
  if (pageType === "tag") return `/blog/tags/${requireSegment(input.slug, "tag")}`;
  if (pageType === "pillar") return `/blog/pillar/${requireSegment(input.slug, "pillar slug")}`;
  if (pageType === "country" && input.countryCode) {
    return `/blog/countries/${requireSegment(input.countryCode, "country code")}`;
  }

  const slug = requireSegment(input.slug, "slug");
  if (pageType === "city") return `/blog/cities/${slug}`;
  if (pageType === "role") return `/blog/roles/${slug}`;
  if (pageType === "visa") return `/blog/visa/${slug}`;
  if (pageType === "university") return `/blog/universities/${slug}`;
  return `/blog/${slug}`;
}

/** Localize a de-localized path with an unprefixed English base locale. */
export function localizeBlogPath(path: string, localeValue: string): string {
  const locale = assertSupportedBlogLocale(localeValue);
  const normalizedPath = `/${path}`.replace(/\/{2,}/g, "/").replace(/\/+$/, "") || "/";
  return locale === DEFAULT_LOCALE
    ? normalizedPath
    : `/${locale}${normalizedPath === "/" ? "" : normalizedPath}`;
}

export function getBlogUrl(input: BlogUrlInput): BlogUrl {
  const locale = assertSupportedBlogLocale(input.locale);
  const path = localizeBlogPath(getUnlocalizedBlogPath(input), locale);
  return { locale, path, absolute: `${BLOG_SITE_URL}${path}` };
}

/**
 * Build evidence-based hreflang links. x-default selects English when a real
 * English equivalent is present; otherwise it follows the current canonical.
 */
export function getBlogLanguageLinks(
  selfInput: BlogAlternateInput,
  alternateInput?: BlogAlternateInput | null,
): BlogLanguageLink[] {
  const self = getBlogUrl(selfInput);
  const alternate = alternateInput ? getBlogUrl(alternateInput) : null;
  const actual = [self, ...(alternate ? [alternate] : [])];
  const byLocale = new Map<SupportedLocale, BlogUrl>();
  for (const url of actual) {
    if (!byLocale.has(url.locale)) byLocale.set(url.locale, url);
  }
  const unique = [...byLocale.values()];
  const xDefault = byLocale.get(DEFAULT_LOCALE) ?? self;

  return [
    ...unique.map((url) => ({ hrefLang: url.locale, href: url.absolute }) as BlogLanguageLink),
    { hrefLang: "x-default", href: xDefault.absolute },
  ];
}

export function blogRecordKey(input: BlogUrlInput): string {
  return `${assertSupportedBlogLocale(input.locale)}:${getBlogUrl(input).path}`;
}

export function getLegacyStaticBlogRedirect(
  pathname: string,
  posts: ReadonlyArray<{ slug: string; locale: string }>,
): string | null {
  const match = pathname.match(/^\/blog\/([^/]+)\/?$/);
  if (!match) return null;
  const post = posts.find((candidate) => candidate.slug === match[1]);
  if (!post || post.locale === DEFAULT_LOCALE) return null;
  return getBlogUrl({ locale: post.locale, pageType: "editorial", slug: post.slug }).path;
}

export function isBlogPath(pathname: string): boolean {
  const withoutLocale = pathname.replace(
    new RegExp(`^/(?:${SUPPORTED_LOCALES.filter((locale) => locale !== DEFAULT_LOCALE).join("|")})(?=/|$)`),
    "",
  );
  return withoutLocale === "/blog" || withoutLocale.startsWith("/blog/");
}
